import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import alertify from 'alertifyjs';
import { LandingNavbarComponent } from '../../components/landing-navbar/landing-navbar.component';
import { AuthHeroSectionComponent } from '../../components/auth-hero-section/auth-hero-section.component';
import {
  LoginApiResponse,
  LoginRequest,
  LoginResponse,
  TOTPLoginChallengeData,
  TOTPLoginChallengeResponse,
  TOTPLoginVerifyRequest,
} from './login-model';
import { ApiServiceService } from '../../modules/services/api-service.service';
import { AuthenticationService } from '../../modules/services/authentication.service';
import { SettingsService } from '../../modules/services/settings/settings.service';
import { WorkflowNotificationService } from '../../modules/services/workflow-notification.service';

@Component({
  selector: 'app-login-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    LandingNavbarComponent,
    AuthHeroSectionComponent,
  ],
  templateUrl: './login-page.html',
  styleUrl: './login-page.scss',
})
export class LoginPage implements AfterViewInit, OnDestroy {
  showPassword = false;
  readonly loginForm;
  readonly totpForm;
  isSubmitting = false;
  isVerifyingTOTP = false;
  submitError = '';
  pendingTOTPChallenge: TOTPLoginChallengeData | null = null;
  verifiedLoginEmail = '';
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private apiService: ApiServiceService,
    private readonly authenticationService: AuthenticationService,
    private readonly router: Router,
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly settingsService: SettingsService,
    private readonly workflowNotificationService: WorkflowNotificationService,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);

    this.loginForm = this.formBuilder.group({
      email: [
        this.authenticationService.getRememberedLoginEmail(),
        [Validators.required, Validators.email],
      ],
      password: ['', Validators.required],
      rememberMe: [false],
    });

    this.totpForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    });
  }

  ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            this.renderer.addClass(entry.target, 'visible');
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const revealEls =
      this.host.nativeElement.querySelectorAll('.scroll-reveal');
    revealEls.forEach((el) => this.intersectionObserver?.observe(el));
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  get emailControl() {
    return this.loginForm.controls.email;
  }

  get passwordControl() {
    return this.loginForm.controls.password;
  }

  get totpCodeControl() {
    return this.totpForm.controls.code;
  }

  get emailErrorMessage(): string {
    if (!this.emailControl.touched && !this.emailControl.dirty) return '';
    if (this.emailControl.hasError('required'))
      return 'Email address is required.';
    if (this.emailControl.hasError('email'))
      return 'Enter a valid email address.';
    return '';
  }

  get passwordErrorMessage(): string {
    if (!this.passwordControl.touched && !this.passwordControl.dirty) return '';
    if (this.passwordControl.hasError('required'))
      return 'Password is required.';
    return '';
  }

  get totpCodeErrorMessage(): string {
    if (!this.totpCodeControl.touched && !this.totpCodeControl.dirty) return '';
    if (this.totpCodeControl.hasError('required'))
      return 'Authenticator code is required.';
    if (this.totpCodeControl.hasError('pattern'))
      return 'Enter the 6-digit authenticator code.';
    return '';
  }

  get canSubmit(): boolean {
    return (
      this.loginForm.valid && !this.isSubmitting && !this.pendingTOTPChallenge
    );
  }

  get canVerifyTOTP(): boolean {
    return this.totpForm.valid && !this.isVerifyingTOTP;
  }

  get challengeExpiresAtText(): string {
    if (!this.pendingTOTPChallenge?.expires_at) return '';

    const expiresAt = new Date(this.pendingTOTPChallenge.expires_at);
    if (Number.isNaN(expiresAt.getTime())) return '';

    return expiresAt.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  submit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      alertify.error('Enter your email and password to continue.');
      return;
    }

    const formValue: LoginRequest = this.loginForm.getRawValue();
    if (formValue.rememberMe) {
      this.authenticationService.saveRememberedLoginEmail(formValue.email);
    } else {
      this.authenticationService.clearRememberedLoginEmail();
    }

    const payload = {
      email: formValue.email,
      password: formValue.password,
    };

    this.isSubmitting = true;
    this.submitError = '';

    this.apiService.loginPost_('/login', payload).subscribe({
      next: (response: LoginApiResponse) => {
        this.isSubmitting = false;

        if (response.status !== 'success' || !response.data) {
          this.submitError =
            response.message || 'Unable to sign in. Please try again.';
          alertify.error(this.submitError);
          return;
        }

        if (this.isTOTPChallengeResponse(response)) {
          this.pendingTOTPChallenge = response.data;
          this.verifiedLoginEmail = formValue.email;
          this.totpForm.reset({ code: '' });
          alertify.success(response.message || 'Authenticator code required.');
          return;
        }

        if (!this.isLoginResponse(response)) {
          this.submitError = 'Unexpected login response. Please try again.';
          alertify.error(this.submitError);
          return;
        }

        this.completeLogin(response, false);
      },
      error: (error) => {
        this.submitError =
          typeof error === 'string'
            ? error
            : 'Unable to sign in. Please try again.';
        alertify.error(this.submitError);
        this.isSubmitting = false;
      },
    });
  }

  verifyTOTP(): void {
    if (this.totpForm.invalid || !this.pendingTOTPChallenge) {
      this.totpForm.markAllAsTouched();
      alertify.error('Enter the 6-digit code from your authenticator app.');
      return;
    }

    const payload: TOTPLoginVerifyRequest = {
      challenge_id: this.pendingTOTPChallenge.challenge_id,
      code: this.totpForm.controls.code.value,
    };

    this.isVerifyingTOTP = true;
    this.submitError = '';

    this.apiService.verifyTOTPLogin(payload).subscribe({
      next: (response: LoginResponse) => {
        this.isVerifyingTOTP = false;

        if (response.status !== 'success' || !response.data) {
          this.submitError =
            response.message || 'Unable to verify authenticator code.';
          alertify.error(this.submitError);
          return;
        }

        this.completeLogin(response, true);
      },
      error: (error) => {
        this.submitError =
          typeof error === 'string'
            ? error
            : 'Unable to verify authenticator code.';
        alertify.error(this.submitError);
        this.isVerifyingTOTP = false;
      },
    });
  }

  onTOTPCodeInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    if (!input) return;

    const normalized = input.value.replace(/\D/g, '').slice(0, 6);
    if (input.value !== normalized) {
      input.value = normalized;
    }
    this.totpForm.controls.code.setValue(normalized, { emitEvent: false });
  }

  resetTOTPChallenge(): void {
    this.pendingTOTPChallenge = null;
    this.verifiedLoginEmail = '';
    this.totpForm.reset({ code: '' });
    this.loginForm.controls.password.reset('');
  }

  private completeLogin(
    response: LoginResponse,
    isTOTPVerified: boolean,
  ): void {
    const sessionResponse: LoginResponse = {
      ...response,
      data: {
        ...response.data,
        is_totp_enabled:
          isTOTPVerified || response.data.is_totp_enabled || false,
      },
    };

    this.authenticationService.saveAuthenticatedSession(sessionResponse);
    this.workflowNotificationService.connect();
    alertify.success(response.message || 'Login successful');

    this.pendingTOTPChallenge = null;
    this.verifiedLoginEmail = '';
    this.settingsService.resolveApplicationColorScheme();
    this.routeAuthenticatedUser(response.data.account_type);
  }

  private routeAuthenticatedUser(accountType: string): void {
    if (accountType === 'ADMIN' || accountType === 'admin') {
      void this.router.navigate(['/admin']);
      return;
    }
    if (accountType === 'USER') {
      void this.router.navigate(['/user']);
      return;
    }
    void this.router.navigate(['/']);
  }

  private isTOTPChallengeResponse(
    response: LoginApiResponse,
  ): response is TOTPLoginChallengeResponse {
    return (
      !!response.data &&
      'requires_totp' in response.data &&
      response.data.requires_totp === true
    );
  }

  private isLoginResponse(
    response: LoginApiResponse,
  ): response is LoginResponse {
    return !!response.data && 'account_type' in response.data;
  }
}
